'use client';

import { UUID } from '@trucksmarter/shared';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

const LoadBoardIncomingLoadIdsByQueryStringContext = createContext<Map<
  string,
  Set<UUID>
> | null>(null);

export function useIncomingLoadIds(queryString: string) {
  return (
    useContext(LoadBoardIncomingLoadIdsByQueryStringContext)?.get(
      queryString,
    ) ?? null
  );
}
const LoadBoardAddIncomingLoadIdsByQueryStringContext = createContext<
  ((queryString: string, ids: Set<UUID>) => void) | null
>(null);

export function useAddIncomingLoadIds(queryString: string) {
  const addIncomingLoadIds = useContext(
    LoadBoardAddIncomingLoadIdsByQueryStringContext,
  );

  if (addIncomingLoadIds === null) {
    throw new Error(
      'useAddIncomingLoadIds has to be used within <LoadBoardIncomingLoadIdsByQueryStringProvider>',
    );
  }

  return (ids: Set<UUID>) => addIncomingLoadIds(queryString, ids);
}

interface Props {
  queryStrings: string[];
}

export default function LoadBoardIncomingLoadIdsByQueryStringProvider({
  queryStrings,
  children,
}: PropsWithChildren<Props>) {
  const [value, setValue] = useState(() => new Map<string, Set<UUID>>());

  const addIncomingLoadIds = useCallback(
    (queryString: string, ids: Set<UUID>) =>
      setValue((prev) => {
        const next = new Map<string, Set<UUID>>(prev);
        const currentIds = next.get(queryString) ?? new Set<UUID>();

        next.set(queryString, new Set([...currentIds, ...ids]));

        return next;
      }),
    [],
  );

  useEffect(() => {
    setValue((prev) => {
      const next = new Map<string, Set<UUID>>();

      queryStrings.forEach((queryString) =>
        next.set(queryString, prev.get(queryString) ?? new Set<UUID>()),
      );

      return next;
    });
  }, [queryStrings]);

  return (
    <LoadBoardIncomingLoadIdsByQueryStringContext.Provider value={value}>
      <LoadBoardAddIncomingLoadIdsByQueryStringContext.Provider
        value={addIncomingLoadIds}
      >
        {children}
      </LoadBoardAddIncomingLoadIdsByQueryStringContext.Provider>
    </LoadBoardIncomingLoadIdsByQueryStringContext.Provider>
  );
}
